import { svgCheck } from "./conversion-flow.svgs.js";
import cartState from "./conversion-flow.cart-state.js";
import { mergeQuizAnswersWithQueryParams } from "./conversion-flow.resolve-variant.js";

const regexFilterByAgePuppy = /^(Puppy|Cachorro|Cucciolo).*/;
const regexFilterByAgeAdult = /^(Adulto).*/;
const regexFilterByAgeSenior = /^(Sénior|Senior|Anziano).*/;
const regexFilterByWeight = (weight) => new RegExp(`(${weight}kg)$`);
const regexFilterGrainFreeChickenStandard =
  /^(Frango & peru fresco|Pollo y pavo fresco|Pollo e tacchino freschi)$/;
const regexFilterGrainFreeChickenLight =
  /^(Frango & peru fresco light|Pollo y pavo fresco light|Pollo e tacchino freschi Light)$/;
const regexFilterGrainFreeChickenSmallKibble =
  /^(Frango & peru fresco · biscoito mini|Pollo y pavo fresco · Croquetas pequeñas|Pollo e tacchino freschi · Piccole crocchette)$/;
const regexFilterGrainFreeSalmonStandard =
  /^(Salmão fresco|Salmón fresco|Salmone fresco)$/;
const regexFilterGrainFreeSalmonSmallKibble =
  /^(Salmão fresco · biscoito mini|Salmón fresco · Croquetas pequeñas|Salmone fresco · Piccole crocchette)$/;
const regexFilterGrainFreeHypo = /^(Hipoalergénica)$/;

function formatVariantData(variantData) {
  const {
    weight,
    price: original_price,
    compared_at_price,
    selling_plan_allocations,
  } = variantData;
  const price = selling_plan_allocations?.[0]?.price ?? original_price;
  return {
    ...variantData,
    formattedWeight: `${(weight / 1000).toFixed(0)}kg`,
    formattedPrice: `€${(price / 100).toFixed(2).replace(".", ",")}`,
    formattedOriginalPrice: `€${(original_price / 100).toFixed(2).replace(".", ",")}`,
    formattedComparedAtPrice: `€${((compared_at_price || price) / 100)
      .toFixed(2)
      .replace(".", ",")}`,
  };
}

function getVariantFilters(variantData) {
  const { option1, option2, option3 } = variantData;
  return [
    [regexFilterByAgePuppy, regexFilterByAgeAdult, regexFilterByAgeSenior].find(
      (reg) => reg.test(option1),
    ),
    [
      regexFilterGrainFreeChickenStandard,
      regexFilterGrainFreeChickenLight,
      regexFilterGrainFreeChickenSmallKibble,
      regexFilterGrainFreeSalmonStandard,
      regexFilterGrainFreeSalmonSmallKibble,
      regexFilterGrainFreeHypo,
    ].find((reg) => reg.test(option2)),
    [
      regexFilterByWeight(3),
      regexFilterByWeight(6),
      regexFilterByWeight(9),
      regexFilterByWeight(12),
      regexFilterByWeight(18),
      regexFilterByWeight(24),
    ].find((reg) => reg.test(option3)),
  ];
}

function getVariantsByFilter(variants, filters) {
  let result = [...variants];
  for (const filter of filters) {
    result = result.filter(
      ({ option1, option2, option3 }) =>
        filter.test(option1) || filter.test(option2) || filter.test(option3),
    );
  }
  return result.map(formatVariantData);
}

class CFBuyCardKibble extends HTMLElement {
  #variants;
  #currentVariantId;
  #currentVariantData;
  #suggestedVariantId;
  #suggestedVariantData;
  #ageFilter;
  #proteinFilter;
  #weightFilter;

  #elementSelect;
  #elementCheckbox;

  constructor() {
    super();
    this.#variants = JSON.parse(this.dataset.variants);
    this.onSelectChange = this.onSelectChange.bind(this);
    this.onCheckboxChange = this.onCheckboxChange.bind(this);
    this.updateCta = this.updateCta.bind(this);
  }

  connectedCallback() {
    const quizAnswers = mergeQuizAnswersWithQueryParams(
      Shopify.designMode
        ? { weight: this.dataset.previewWeight }
        : QuizStorage.getQuizAnswers(),
    );
    const suggestedVariants = ProductConfig.getProductVariants(quizAnswers);
    const variants = ProductConfig.getProductVariants({
      ...quizAnswers,
      foodBagWeight: "3",
    });

    this.#suggestedVariantId = +suggestedVariants.monthly.variantId;
    this.#suggestedVariantData = formatVariantData(
      this.#variants.find(({ id }) => id === this.#suggestedVariantId),
    );

    this.#currentVariantId = +variants.monthly.variantId;
    this.#currentVariantData = formatVariantData(
      this.#variants.find(({ id }) => id === this.#currentVariantId),
    );

    const [ageFilter, proteinFilter, weightFilter] = getVariantFilters(
      this.#currentVariantData,
    );
    this.#ageFilter = ageFilter;
    this.#proteinFilter = proteinFilter;
    this.#weightFilter = weightFilter;

    this.render();
  }

  render() {
    const weightVariants = getVariantsByFilter(this.#variants, [
      this.#ageFilter,
      this.#proteinFilter,
    ]);

    const title = this.dataset.title;
    const images = JSON.parse(this.dataset.images);

    const bullet_points = (
      this.#currentVariantData.metafields.bullet_points || []
    )
      .map(
        (text) => html`
          <div class="body-list-item">
            <div class="bullet-point">${svgCheck}</div>
            <span class="list-item-text">${text}</span>
          </div>
        `,
      )
      .join("");

    const showOriginalPrice =
      this.#currentVariantData.formattedPrice !==
      this.#currentVariantData.formattedOriginalPrice;

    this.innerHTML = html`
      <div class="product-card">
        <div class="product-card-header">
          <span class="title">Base</span>
          <span class="original-price"
            >${showOriginalPrice
              ? this.#currentVariantData.formattedOriginalPrice
              : ""}</span
          >
          <span class="price">${this.#currentVariantData.formattedPrice}</span>
          <label class="check">
            <input type="checkbox" checked="true" style="display:none;" />
            ${svgCheck}
          </label>
        </div>

        <div class="product-card-body">
          <div class="product-card-details">
            <span class="body-title">${title}</span>
            <span class="body-subtitle"
              >${this.#currentVariantData.metafields.subtitle}</span
            >
            <div class="body-list">${bullet_points}</div>
          </div>
          <div class="product-image">
            <img src="${images[0]?.src ?? images[0] ?? ""}" alt="kibble" />
          </div>
        </div>

        <div class="product-card-footer">
          <div class="size-selector-row">
            <select class="size-select" role="listbox">
              ${weightVariants
                .map(
                  ({ id, formattedWeight }) =>
                    `<option value="${id}">${formattedWeight}</option>`,
                )
                .join("")}
            </select>
            <div class="size-select-chevron">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="feather feather-chevron-down"
                role="presentation"
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </div>
          </div>
          <span class="suggested-amount">
            Quantidade sugerida para uma dieta mista com refeições frescas:
            ${this.#suggestedVariantData.formattedWeight}
          </span>
        </div>
      </div>
    `;

    this.#elementSelect = this.querySelector(".size-select");
    this.#elementSelect.addEventListener("change", this.onSelectChange);

    this.#elementCheckbox = this.querySelector("input[type=checkbox]");
    this.#elementCheckbox.addEventListener("change", this.onCheckboxChange);

    this.querySelector(".size-select-chevron").addEventListener(
      "click",
      (ev) => {
        ev.preventDefault();
        this.#elementSelect.showPicker();
      },
    );
    this.updateCta();
  }

  onSelectChange(ev) {
    this.#currentVariantId = +ev.target.value;
    this.#currentVariantData = formatVariantData(
      this.#variants.find(({ id }) => id === this.#currentVariantId),
    );
    this.querySelector(".product-card-header .price").textContent =
      this.#currentVariantData.formattedPrice;

    const showOriginalPrice =
      this.#currentVariantData.formattedPrice !==
      this.#currentVariantData.formattedOriginalPrice;
    this.querySelector(".product-card-header .original-price").textContent =
      showOriginalPrice ? this.#currentVariantData.formattedOriginalPrice : "";
    this.updateCta();
  }

  onCheckboxChange() {
    this.updateCta();
  }

  updateCta() {
    cartState.update("KIBBLE", {
      checked: this.#elementCheckbox.checked,
      original_price: this.#currentVariantData.price,
      price:
        this.#currentVariantData.selling_plan_allocations?.[0]?.price ||
        this.#currentVariantData.price,
      quantity: 1,
      variantId: this.#currentVariantId,
      selling_plan:
        this.#currentVariantData.selling_plan_allocations?.[0]?.selling_plan_id,
    });
  }
}

if (!customElements.get("cf-buy-card-kibble")) {
  customElements.define("cf-buy-card-kibble", CFBuyCardKibble);
}
